import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, AlertOctagon } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { resetAuthBounces } from "../lib/authLoopBreaker";

/**
 * AuthCallback · Sovereign-managed Google Auth landing page.
 *
 * The frontend redirected to the Sovereign Auth proxy,
 * Google completed, and the browser is now back at:
 *   <origin>/auth/callback#session_id=XYZ
 *
 * This component runs SYNCHRONOUSLY during the first render (per the
 * integration playbook race-condition guidance), POSTs the session_id
 * to the backend, lets `set_auth_cookies` install the EIGOO JWT, then
 * pushes the user to /bridge.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
 * URLS, THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
    const navigate = useNavigate();
    const location = useLocation();
    const { setUser } = useAuth();
    const [status, setStatus] = useState("processing");
    const [err, setErr] = useState("");
    // useRef (not useState) — survives StrictMode double-mount in dev so
    // the same session_id can't be redeemed twice.
    const hasProcessed = useRef(false);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const hash = location.hash || window.location.hash || "";
        const match = hash.match(/session_id=([^&]+)/);
        if (!match) {
            setErr("No session_id received from Google. Please retry.");
            setStatus("error");
            return;
        }
        const sessionId = decodeURIComponent(match[1]);

        (async () => {
            try {
                const { data } = await api.post("/auth/social/google/session", {
                    session_id: sessionId,
                });
                // Cookie is set by the backend response. Hydrate AuthContext
                // immediately so ProtectedRoute doesn't re-fetch /auth/me
                // before navigation completes.
                setUser(data);
                resetAuthBounces();
                // Strip the session_id from history so a refresh doesn't
                // re-redeem a now-consumed token.
                window.history.replaceState({}, document.title, "/auth/callback");
                navigate("/bridge", { replace: true });
            } catch (e) {
                setErr(
                    formatApiError(e.response?.data?.detail) ||
                        e.message ||
                        "Could not complete Google sign-in."
                );
                setStatus("error");
            }
        })();
    }, [location.hash, navigate, setUser]);

    return (
        <div
            data-testid="auth-callback"
            className="min-h-screen flex items-center justify-center px-6 text-white"
            style={{
                background:
                    "radial-gradient(ellipse at center, #0D1824 0%, #050A0F 70%, #02060A 100%)",
                fontFamily: "'Inter', sans-serif",
            }}
        >
            <div className="max-w-md w-full text-center">
                <div
                    className="text-[11px] uppercase tracking-[0.4em] mb-6"
                    style={{
                        color: status === "error" ? "#FF6B6B" : "#00E5FF",
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 800,
                    }}
                >
                    ◢ Sovereign Auth Handshake
                </div>
                {status === "processing" ? (
                    <>
                        <h1
                            className="text-3xl md:text-4xl uppercase font-black tracking-tight"
                            style={{ fontFamily: "'Montserrat', sans-serif", color: "#F8FAFC" }}
                        >
                            Patching Identity
                        </h1>
                        <div className="mt-8 flex items-center justify-center gap-2">
                            <Loader2 size={18} className="animate-spin" style={{ color: "#E8C56C" }} />
                            <span
                                className="text-[10px] uppercase tracking-[0.4em]"
                                style={{ color: "#829AB1", fontFamily: "'Montserrat', sans-serif" }}
                            >
                                Verifying with Sovereign Auth Service
                            </span>
                        </div>
                    </>
                ) : (
                    <>
                        <h1
                            className="text-3xl md:text-4xl uppercase font-black tracking-tight"
                            style={{ fontFamily: "'Montserrat', sans-serif", color: "#F8FAFC" }}
                            data-testid="auth-callback-error"
                        >
                            Handshake Failed
                        </h1>
                        <p className="mt-4 text-sm" style={{ color: "#A9BCD2" }}>
                            <AlertOctagon size={16} className="inline-block mr-1.5" style={{ color: "#FF6B6B" }} />
                            {err}
                        </p>
                        <button
                            onClick={() => navigate("/login", { replace: true })}
                            data-testid="auth-callback-back"
                            className="mt-8 inline-flex items-center justify-center gap-2 px-6 py-3 uppercase tracking-[0.25em] text-xs font-bold"
                            style={{
                                fontFamily: "'Montserrat', sans-serif",
                                color: "#E8C56C",
                                border: "1px solid #E8C56C",
                                minHeight: 44,
                            }}
                        >
                            [ RETURN TO LOGIN ]
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
